import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import "./Home.css";

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import FeaturedAccordion from './FeaturedAccordion';
import useFetch from "./ApiClient/GetApi";
import { PostApi } from "./ApiClient/PostApi";
import VideoPlayer from "./VideoPlayer/VideoPlayer";

gsap.registerPlugin(ScrollTrigger);

// ─────────────────────────────────────────
// Helpers (define ABOVE component)
// ─────────────────────────────────────────

function plainSupport(htmlOrText = "", maxWords = 200) {
  if (!htmlOrText) return "";
  // Strip HTML if any
  if (/<[a-z][\s\S]*>/i.test(htmlOrText)) {
    const div = document.createElement("div");
    div.innerHTML = htmlOrText;
    div.querySelectorAll("script,style,noscript,iframe,svg").forEach(el => el.remove());
    htmlOrText = (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
  }
  const words = htmlOrText.trim().split(/\s+/);
  return words.length <= maxWords ? htmlOrText : words.slice(0, maxWords).join(" ") + "…";
}

function tagClassFor(name = "News") {
  const key = (name || "News").toLowerCase();
  if (["news"].includes(key)) return "news";
  if (["tech", "technology"].includes(key)) return "tech";
  if (["sports", "sport"].includes(key)) return "sports";
  if (["transport"].includes(key)) return "transport";
  if (["entertainment"].includes(key)) return "entertainment";
  if (["update", "updates", "review"].includes(key)) return "update";
  return "lifestyle";
}

function pickTag(row) {
  const fromCat = row?.category?.name;
  if (fromCat) return fromCat;
  const firstTag = (row?.tags || "").split(",")[0]?.trim();
  return firstTag || "News";
}

// Convert backend row → QuickRead card shape
function toQuickItem(row, supportWords = 200) {
  const tag = pickTag(row);
  return {
    id: row.id,
    img: row.thumbnail || row.cover || "/20.jpg",
    tag,
    tagClass: tagClassFor(tag),
    headline: row.title || row.seo_title || "Untitled",
    slug: row.slug,
    support: plainSupport(row.seo_description || row.summary || row.content || "", supportWords),
    to: `/article/${row.slug}`
  };
}

export default function Subcategory() {
  const location = useLocation();
  const { subCategoryName, categoryName } = useParams();

  const subCategoryId = location.state?.subCategoryId;

  // Data from API
  const { apiData } = useFetch(`/user/subCategoryNews/${subCategoryId}`);

  // Quick Read (dynamic + infinite)
  const [quickReadItems, setQuickReadItems] = useState([]);

  // Infinite scroll state for Quick Read
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loaderRef = useRef(null);
  const observerRef = useRef(null);

  // GSAP refs (kept from your code)
  const bannerAdRef = useRef(null);
  const bannerTextRef = useRef(null);
  const bannerBtnRef = useRef(null);
  const bannerAd1Ref = useRef(null);
  const bannerText1Ref = useRef(null);
  const bannerBtn1Ref = useRef(null);
  const lunaticBannerRef = useRef(null);
  const lunaticOverlayRef = useRef(null);
  const lunaticLogoRef = useRef(null);
  const lunaticTitleRef = useRef(null);
  const lunaticDescRef = useRef(null);
  const lunaticBtnRef = useRef(null);

  // ─────────────────────────────────────────
  // Build Quick Read from initial API payload
  // ─────────────────────────────────────────
  useEffect(() => {
    const raw = apiData?.data?.quickRead ?? [];
    const arr = Array.isArray(raw) ? raw : [];
    setQuickReadItems(arr.map(row => toQuickItem(row, 200)));
  }, [apiData]);

  // ─────────────────────────────────────────
  // GSAP (same as your version; you can trim if desired)
  // ─────────────────────────────────────────
  useEffect(() => {
    gsap.set([bannerAdRef.current, bannerAd1Ref.current], {
      opacity: 0, y: 100, scale: 0.9, rotationX: 15
    });
    gsap.set([bannerTextRef.current, bannerText1Ref.current], {
      opacity: 0, y: 60, scale: 0.8
    });
    gsap.set([bannerBtnRef.current, bannerBtn1Ref.current], {
      opacity: 0, y: 40, scale: 0.7
    });

    if (bannerAdRef.current) {
      const bannerTl = gsap.timeline({
        scrollTrigger: { trigger: bannerAdRef.current, start: "top 80%", end: "bottom 20%", toggleActions: "play none none reverse" }
      });
      bannerTl
        .to(bannerAdRef.current, { opacity: 1, y: 0, scale: 1, rotationX: 0, duration: 1.2, ease: "back.out(1.7)" })
        .to(bannerTextRef.current, { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: "elastic.out(1, 0.3)" }, "-=0.6")
        .to(bannerBtnRef.current, { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: "back.out(1.7)" }, "-=0.4");
    }

    if (bannerAd1Ref.current) {
      const banner1Tl = gsap.timeline({
        scrollTrigger: { trigger: bannerAd1Ref.current, start: "top 80%", end: "bottom 20%", toggleActions: "play none none reverse" }
      });
      banner1Tl
        .to(bannerAd1Ref.current, { opacity: 1, y: 0, scale: 1, rotationX: 0, duration: 1.2, ease: "back.out(1.7)" })
        .to(bannerText1Ref.current, { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: "elastic.out(1, 0.3)" }, "-=0.6")
        .to(bannerBtn1Ref.current, { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: "back.out(1.7)" }, "-=0.4");
    }

    gsap.to([bannerAdRef.current, bannerAd1Ref.current], {
      yPercent: -10, ease: "none",
      scrollTrigger: { trigger: bannerAdRef.current, start: "top bottom", end: "bottom top", scrub: 1 }
    });

    gsap.to([bannerTextRef.current, bannerText1Ref.current], {
      y: -5, duration: 2, ease: "power2.inOut", yoyo: true, repeat: -1
    });

    gsap.to([bannerBtnRef.current, bannerBtn1Ref.current], {
      scale: 1.05, duration: 1.5, ease: "power2.inOut", yoyo: true, repeat: -1
    });

    gsap.set(lunaticBannerRef.current, { opacity: 0, y: 100, scale: 0.95 });
    gsap.set(lunaticOverlayRef.current, { opacity: 0, y: 50 });
    gsap.set([lunaticLogoRef.current, lunaticTitleRef.current], { opacity: 0, y: 30, scale: 0.8 });
    gsap.set(lunaticDescRef.current, { opacity: 0, y: 40 });
    gsap.set(lunaticBtnRef.current, { opacity: 0, y: 20, scale: 0.7 });

    if (lunaticBannerRef.current) {
      const lunaticTl = gsap.timeline({
        scrollTrigger: { trigger: lunaticBannerRef.current, start: "top 80%", end: "bottom 20%", toggleActions: "play none none reverse" }
      });
      lunaticTl
        .to(lunaticBannerRef.current, { opacity: 1, y: 0, scale: 1, duration: 1.5, ease: "power3.out" })
        .to(lunaticOverlayRef.current, { opacity: 1, y: 0, duration: 1, ease: "power2.out" }, "-=1.2")
        .to(lunaticLogoRef.current, { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: "back.out(1.7)" }, "-=0.8")
        .to(lunaticTitleRef.current, { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: "back.out(1.7)" }, "-=0.6")
        .to(lunaticDescRef.current, { opacity: 1, y: 0, duration: 1, ease: "power2.out" }, "-=0.4")
        .to(lunaticBtnRef.current, { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: "back.out(1.7)" }, "-=0.6");
    }

    gsap.to(lunaticBannerRef.current, {
      yPercent: -15, ease: "none",
      scrollTrigger: { trigger: lunaticBannerRef.current, start: "top bottom", end: "bottom top", scrub: 1 }
    });

    gsap.to(lunaticLogoRef.current, {
      y: -8, duration: 3, ease: "power2.inOut", yoyo: true, repeat: -1
    });

    if (lunaticDescRef.current) {
      const descText = lunaticDescRef.current.textContent || "";
      if (descText) {
        lunaticDescRef.current.innerHTML = "";
        const words = descText.split(' ');
        words.forEach((word, index) => {
          const span = document.createElement('span');
          span.textContent = word + ' ';
          span.style.opacity = '0';
          span.style.transform = 'translateY(20px)';
          span.style.display = 'inline-block';
          span.style.marginRight = '4px';
          lunaticDescRef.current.appendChild(span);

          gsap.to(span, {
            opacity: 1, y: 0, duration: 0.4, delay: 1.5 + (index * 0.05), ease: "power2.out",
            scrollTrigger: { trigger: lunaticDescRef.current, start: "top 80%", toggleActions: "play none none reverse" }
          });
        });
      }
    }

    gsap.to(lunaticBtnRef.current, {
      scale: 1.05, duration: 2, ease: "power2.inOut", yoyo: true, repeat: -1
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  // ─────────────────────────────────────────
  // Infinite Scroll (Quick Read ONLY)
  // ─────────────────────────────────────────

  // Reset paging when subcategory/category changes
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    setLoadingMore(false);
    // keep the already shown quickReadItems from first payload
  }, [subCategoryId, categoryName]);

  const getRootEl = () => null;
  const isInView = (el, rootEl = null, extra = 0) => {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    let viewTop = 0, viewBottom = window.innerHeight;
    if (rootEl) {
      const r = rootEl.getBoundingClientRect();
      viewTop = r.top; viewBottom = r.bottom;
    }
    return rect.top < viewBottom + extra && rect.bottom > viewTop - extra;
  };

  // excludeIds: main banners + already in quick read
  const excludeIds = useMemo(() => {
    const ids = new Set();
    (apiData?.data?.mainNews || []).forEach(row => row?.news?.id && ids.add(row.news.id));
    quickReadItems.forEach(q => q?.id && ids.add(q.id));
    return Array.from(ids);
  }, [apiData, quickReadItems]);

  const fetchMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const payload = {
        category: categoryName, // backend filters by category string
        page,
        limit: 8,
        excludeIds,
      };

      const res = await PostApi("/user/getCategoryMoreNews", payload);
      const ok = res?.data?.status === 1 || res?.data?.status === "1";
      const data = res?.data?.data || {};
      const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
      const nextHasMore = typeof data?.hasMore === "boolean" ? data.hasMore : (items.length > 0);

      // Append to Quick Read ONLY (de-duped)
      setQuickReadItems(prev => {
        const seen = new Set(prev.map(q => q.id));
        excludeIds.forEach(id => seen.add(id));
        const incoming = items
          .filter(n => n?.id && !seen.has(n.id))
          .map(n => toQuickItem(n, 200));
        return incoming.length ? [...prev, ...incoming] : prev;
      });

      setHasMore(nextHasMore);
      if (ok && nextHasMore) setPage(p => p + 1);
    } catch (err) {
      console.error("Subcategory.fetchMore error:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [page, hasMore, loadingMore, excludeIds, categoryName]);

  // Build observer under Quick Read section
  useEffect(() => {
    if (!loaderRef.current) return;
    if (observerRef.current) observerRef.current.disconnect();

    const rootEl = getRootEl();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMore && !loadingMore) {
          fetchMore();
        }
      },
      { root: rootEl || null, threshold: 0.01, rootMargin: "800px 0px" }
    );

    observerRef.current.observe(loaderRef.current);
    return () => observerRef.current?.disconnect();
  }, [fetchMore, hasMore, loadingMore, subCategoryId, categoryName]);

  // Kickstart if short page
  useEffect(() => {
    if (!hasMore || loadingMore) return;
    const rootEl = getRootEl();
    const el = loaderRef.current;
    const docShort = rootEl
      ? rootEl.scrollHeight <= rootEl.clientHeight + 10
      : document.body.scrollHeight <= window.innerHeight + 10;

    if (docShort || isInView(el, rootEl, 900)) {
      fetchMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCategoryId, categoryName]);

  // Fallback scroll listener
  useEffect(() => {
    if (!hasMore) return;
    const rootEl = getRootEl();
    const target = rootEl || window;

    const onScroll = () => {
      if (loadingMore) return;
      if (isInView(loaderRef.current, rootEl, 900)) fetchMore();
    };

    target.addEventListener("scroll", onScroll, { passive: true });
    return () => target.removeEventListener("scroll", onScroll);
  }, [hasMore, loadingMore, fetchMore]);

  return (
    <>
      <div className="home-container">
        <main>
           <VideoPlayer url="https://www.youtube.com/watch?v=3VpFFKGCaLQ&list=RD3VpFFKGCaLQ&start_radio=1" />
          {/* NEWS HEADING */}
          <div className="news-heading">
            <Link to="/articles" style={{ textDecoration: 'none', color: 'inherit' }}>
              <h1>
                <span style={{ opacity: '0.3' }}>{categoryName ?? "News"}</span>
                <span style={{ color: '#c20a7c' }}>.</span> {subCategoryName ?? ""}
              </h1>
            </Link>
            <div className="news-heading-border"></div>
          </div>

          {/* Banner Section (first 3 items) */}
          <section className="subcategory-banner-section">
            <div className="subcategory-banner-container">
              {/* Left */}
              <div className="subcategory-banner-left">
                <div className="subcategory-banner-card">
                  <div className="subcategory-banner-image-container">
                    {apiData?.data?.mainNews?.[0]?.news?.thumbnail && (
                      <img
                        src={apiData?.data?.mainNews?.[0]?.news?.thumbnail}
                        alt={apiData?.data?.mainNews?.[0]?.news?.title ?? "News Image"}
                        className="subcategory-banner-image rounded"
                      />
                    )}
                  </div>
                  <div className="subcategory-banner-content">
                    <span className="tag news">NEWS</span>
                    <Link to={`/article/${apiData?.data?.mainNews?.[0]?.news?.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <h2 className="subcategory-banner-title">
                        {apiData?.data?.mainNews?.[0]?.news?.title ?? "Title Missing"}
                      </h2>
                    </Link>
                    <p
                      className="subcategory-banner-description"
                      dangerouslySetInnerHTML={{
                        __html: apiData?.data?.mainNews?.[0]?.news?.content?.slice(0, 200) ?? ""
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Middle */}
              <div className="subcategory-banner-middle">
                <div className="subcategory-banner-middle-content">
                  <span className="tag update">REVIEW</span>
                  <Link to={`/article/${apiData?.data?.mainNews?.[1]?.news?.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <h3 className="subcategory-banner-middle-title">
                      {apiData?.data?.mainNews?.[1]?.news?.title ?? "Second News Title"}
                    </h3>
                  </Link>
                  <div className="subcategory-banner-middle-image-container">
                    <img
                      src={apiData?.data?.mainNews?.[1]?.news?.thumbnail ?? "/default-thumbnail.jpg"}
                      alt="Second News Thumbnail"
                      className="subcategory-banner-middle-image"
                    />
                  </div>
                </div>
              </div>

              {/* Right */}
              <div className="subcategory-banner-right">
                <div className="subcategory-banner-card">
                  <div className="subcategory-banner-image-container-2">
                    <img
                      src={apiData?.data?.mainNews?.[2]?.news?.thumbnail ?? "/default-thumbnail.jpg"}
                      alt="Third News Thumbnail"
                      className="subcategory-banner-image"
                    />
                  </div>
                  <div className="subcategory-banner-content">
                    <span className="tag lifestyle">LIFESTYLE</span>
                    <Link to={`/article/${apiData?.data?.mainNews?.[2]?.news?.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <h3 className="subcategory-banner-title">
                        {apiData?.data?.mainNews?.[2]?.news?.title ?? "Third News Title"}
                      </h3>
                    </Link>
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* Banner Ad */}
          {apiData?.data?.ads?.[0] ? (
            <div ref={bannerAdRef} className="banner-ad" style={{ backgroundImage: `url(${apiData?.data?.ads[0].image})` }}>
              <span ref={bannerTextRef} className="banner-ad-text">{apiData?.data?.ads[0]?.title ?? ""}</span>
              <span ref={bannerBtnRef} className="ad-btn">
                <a
                  style={{ textDecoration: "none" }}
                  href={`https://${apiData?.data?.ads[0].link}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Shop Now
                </a>
              </span>
            </div>
          ) : (
            <div ref={bannerAdRef} className="banner-ad" style={{ backgroundImage: "url('/4.jpg')" }}>
              <span ref={bannerTextRef} className="banner-ad-text">Bramleys.</span>
              <span ref={bannerBtnRef} className="ad-btn">SHOP NOW</span>
            </div>
          )}

          {/* Lunatic Fringe Banner */}
          {apiData?.data?.ads?.[1] ? (
            <div ref={lunaticBannerRef} className="lunatic-banner" style={{ backgroundImage: "url('/12.jpg')" }}>
              <div ref={lunaticOverlayRef} className="lunatic-banner-overlay lunatic-banner-row">
                <div className="lunatic-banner-content-col">
                  <div ref={lunaticTitleRef} className="lunatic-title">{apiData?.data?.ads[1]?.title}</div>
                  <div ref={lunaticDescRef} className="lunatic-desc">{apiData?.data?.ads[1]?.description}</div>
                </div>
                <a
                  href={`https://${apiData?.data?.ads[1].link}`}
                  target="_blank"
                  style={{ textDecoration: "none" }}
                  ref={lunaticBtnRef}
                  className="lunatic-btn"
                >
                  BOOK NOW
                </a>
              </div>
            </div>
          ) : (
            <div ref={lunaticBannerRef} className="lunatic-banner" style={{ backgroundImage: "url('/12.jpg')" }}>
              <div ref={lunaticOverlayRef} className="lunatic-banner-overlay lunatic-banner-row">
                <div className="lunatic-banner-content-col">
                  <div ref={lunaticLogoRef} className="lunatic-logo">If</div>
                  <div ref={lunaticTitleRef} className="lunatic-title">Lunatic Fringe</div>
                  <div ref={lunaticDescRef} className="lunatic-desc">
                    A sanctuary of beauty and artistry, where only you matter. Here, every experience is personal—from the way we greet you at our door to how our skilled stylists and technicians listen intently to understand exactly what you want.
                  </div>
                </div>
                <button ref={lunaticBtnRef} className="lunatic-btn">BOOK NOW</button>
              </div>
            </div>
          )}

          {/* QUICK READ (infinite) */}
          <section className="quickread-section exact-quickread">
            <div className="section-title quickread">QUICK READ</div>

            <div className="quickread-list exact-quickread-list">
              {quickReadItems.map((item, idx) => {
                const elements = [];
                elements.push(
                  <div key={item.id ?? idx} className="quickread-card exact-quickread-card">
                    <img
                      className="quickread-img exact-quickread-img"
                      src={item.img}
                      alt={item.headline}
                      style={{ width: '300px', height: '200px', objectFit: 'cover', minWidth: '300px', minHeight: '200px' }}
                    />
                    <div className="quickread-content exact-quickread-content">
                      <span className={`tag ${item.tagClass} exact-quickread-tag`}>{item.tag}</span>
                      <div className="exact-quickread-body">
                        <Link to={`/article/${item.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                          <div className="quickread-headline exact-quickread-headline">{item.headline}</div>
                        </Link>
                        <div className="exact-quickread-support">{item.support}</div>
                      </div>
                    </div>
                  </div>
                );
                // optional every-4th banner
                if ((idx + 1) % 4 === 0) {
                  const bannerGroup = Math.floor((idx + 1) / 4);
                  const isNewBanner = bannerGroup % 2 === 1;
                  elements.push(
                    <div key={`banner-${idx}`} className="quickread-banner-wrapper">
                      {isNewBanner ? (
                        <div className="banner-ad-1" style={{ backgroundImage: "url('/image.png')" }}>
                          <span className="banner-ad-text">Bramleys.</span>
                          <span className="ad-btn">SHOP NOW</span>
                        </div>
                      ) : (
                        <div className="banner-ad" style={{ backgroundImage: "url('/4.jpg')" }}>
                          <span className="banner-ad-text">Bramleys.</span>
                          <span className="ad-btn">SHOP NOW</span>
                        </div>
                      )}
                    </div>
                  );
                }
                return elements;
              })}
            </div>

            {/* Sentinel / Loader FOR QUICK READ */}
            <div ref={loaderRef} style={{ textAlign: "center", margin: "2rem 0", minHeight: 60 }}>
              {loadingMore && (
                <div
                  className="spinner"
                  style={{
                    width: 38, height: 38,
                    border: "4px solid #c20a7c",
                    borderTop: "4px solid #fff",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    margin: "0 auto",
                  }}
                />
              )}
              {!hasMore && (
                <div style={{ opacity: 0.6, fontSize: 14, padding: 8 }}>
                  You’re all caught up.
                </div>
              )}
            </div>
          </section>
        </main>
      </div>
    </>
  );
}
